import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth, db } from '../firebase/config';
import { doc, getDoc } from 'firebase/firestore';
import { signOut } from 'firebase/auth';
import { theme } from '../styles/GlobalStyles';
import { HeaderWaves } from './WaveShapes';
import { HomeIcon, CalendarIcon, SunIcon, UserIcon, SettingsIcon, BotIcon } from './Icons';

interface AppLayoutProps {
  children: React.ReactNode;
}

interface UserProfileData {
  selectedGradient?: string;
  isUsingGradient?: boolean;
}

const LayoutContainer = styled.div`
  display: flex;
  min-height: 100vh;
  background-color: ${theme.colors.white};
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    flex-direction: column;
    background: ${theme.mobile.gradientBg};
  }
`;

const Header = styled.header`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: 80px;
  background: #ffffff;
  z-index: 1000;
  display: flex;
  align-items: center;
  padding: 0 ${theme.spacing.lg};
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: 0 ${theme.spacing.md};
    height: 70px;
    position: fixed;
  }
`;

const WaveBackground = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1;
  overflow: hidden;
`;

const HeaderContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 ${theme.spacing.lg};
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: relative;
  z-index: 10;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: 0 ${theme.spacing.md};
    flex-wrap: wrap;
    gap: ${theme.spacing.sm};
    width: 100%;
  }
`;

const MobileLeftSection = styled.div`
  display: none;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    display: flex;
    align-items: center;
    position: absolute;
    left: ${theme.spacing.md};
    top: 50%;
    transform: translateY(-50%);
    z-index: 10;
  }
`;

const Logo = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  min-height: 50px;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    min-height: 40px;
    flex: 1;
    justify-content: center;
  }
`;

const LogoImage = styled.img`
  height: 50px;
  width: auto;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    height: 40px;
  }
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.md};
`;

const Sidebar = styled.aside`
  position: fixed;
  left: 0;
  top: 80px;
  width: 220px;
  height: calc(100vh - 80px);
  background-color: #f8f9fa;
  padding: ${theme.spacing.lg};
  overflow-y: auto;
  z-index: 100;
  display: flex;
  flex-direction: column;
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    width: 200px;
    padding: ${theme.spacing.md};
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    top: 70px;
    height: calc(100vh - 70px);
    width: 100%;
    transform: translateX(-100%);
    transition: transform 0.3s ease;
    
    &.open {
      transform: translateX(0);
    }
  }
`;

const ProfileSection = styled.div`
  text-align: center;
  padding: ${theme.spacing.md} 0;
  border-bottom: 1px solid #f0f0f0;
  margin-bottom: ${theme.spacing.md};
`;

const ProfilePicture = styled.div<{ backgroundImage?: string; gradient?: string }>`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: ${props => {
    if (props.backgroundImage) return `url(${props.backgroundImage})`;
    if (props.gradient) return props.gradient;
    return `linear-gradient(135deg, ${theme.colors.primary}, ${theme.colors.tertiary})`;
  }};
  background-size: cover;
  background-position: center;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto ${theme.spacing.sm};
  font-size: 1.5rem;
  color: ${theme.colors.white};
  cursor: pointer;
  transition: all 0.3s ease;
  font-weight: 600;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
  border: 3px solid white;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  
  &:hover {
    transform: scale(1.05);
  }
`;

const ProfileName = styled.h3`
  color: ${theme.colors.deepDark};
  font-size: 0.95rem;
  margin: 0;
  font-weight: 600;
`;

const NavigationSection = styled.div`
  background: #e9ecef;
  border-radius: 16px;
  padding: 6px;
  margin-bottom: ${theme.spacing.lg};
`;

const NavigationGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 3px;
`;

const NavCard = styled.div<{ active?: boolean }>`
  background: ${props => props.active ? '#ffffff' : 'transparent'};
  border: none;
  border-radius: 12px;
  padding: ${theme.spacing.sm} ${theme.spacing.xs};
  text-align: center;
  cursor: pointer;
  transition: all 0.2s ease;
  color: ${props => props.active ? '#2c3e50' : '#6c757d'};
  min-height: 70px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  position: relative;
  
  ${props => props.active && `
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  `}
  
  &:hover {
    background: ${props => props.active ? '#ffffff' : 'rgba(255, 255, 255, 0.5)'};
    ${props => !props.active && `
      box-shadow: 0 1px 4px rgba(0, 0, 0, 0.05);
    `}
  }
`;

const NavIcon = styled.div`
  margin-bottom: 4px;
  display: flex;
  justify-content: center;
  align-items: center;
  height: 24px;
`;

const NavLabel = styled.div`
  font-size: 0.7rem;
  font-weight: 500;
  line-height: 1.1;
  text-align: center;
`;

const SignOutSection = styled.div`
  padding: ${theme.spacing.md} 0;
  border-top: 1px solid #f0f0f0;
  margin-top: auto;
`;

const SignOutButton = styled.button`
  background: none;
  border: none;
  color: #666;
  font-size: 1rem;
  font-weight: 500;
  padding: ${theme.spacing.sm} 0;
  cursor: pointer;
  width: 100%;
  text-align: center;
  transition: all 0.2s ease;
  min-height: ${theme.mobile.minTouchTarget};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${theme.spacing.sm};
  
  &:hover {
    color: #444;
  }
  
  &:active {
    transform: scale(0.98);
  }
`;

const MobileOverlay = styled.div<{ isOpen: boolean }>`
  display: none;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    display: ${props => props.isOpen ? 'block' : 'none'};
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    z-index: 99;
  }
`;

const MainContent = styled.main`
  margin-left: 220px;
  margin-top: 80px;
  flex: 1;
  background-color: #f8f9fa;
  min-height: calc(100vh - 80px);
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    margin-left: 200px;
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    margin-left: 0;
    margin-top: 70px;
    min-height: calc(100vh - 70px);
  }
`;

const MobileMenuButton = styled.button<{ isOpen: boolean }>`
  display: none;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  min-height: ${theme.mobile.minTouchTarget};
  min-width: ${theme.mobile.minTouchTarget};
  align-items: center;
  justify-content: center;
  color: ${props => props.isOpen ? theme.colors.primary : '#666'};
  font-size: 18px;
  font-weight: bold;
  transition: color 0.2s ease;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    display: flex;
  }
  
  &:active {
    transform: scale(0.95);
  }
  
  &:hover {
    color: ${props => props.isOpen ? theme.colors.primary : '#444'};
  }
`;

const AppLayout: React.FC<AppLayoutProps> = ({ children }) => {
  const [user] = useAuthState(auth);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [userProfileData, setUserProfileData] = useState<UserProfileData>({});
  const navigate = useNavigate();
  const location = useLocation();

  const navigationItems = [
    { path: '/app', icon: 'home', label: 'Home' },
    { path: '/planner', icon: 'calendar', label: 'Planner' },
    { path: '/my-day', icon: 'sun', label: 'My Day' },
    { path: '/profile', icon: 'user', label: 'Profile' },
    { path: '/settings', icon: 'settings', label: 'Settings' },
    { path: '/ai-assistant', icon: 'bot', label: 'AI Assistant' },
  ];

  // Load user profile data when user changes
  useEffect(() => {
    const loadUserProfile = async () => {
      if (!user) return;
      
      try {
        const userDocRef = doc(db, 'userProfiles', user.uid);
        const userDoc = await getDoc(userDocRef);
        
        if (userDoc.exists()) {
          const profileData = userDoc.data() as UserProfileData;
          setUserProfileData(profileData);
        } else {
          // Set defaults if no profile exists
          setUserProfileData({
            isUsingGradient: !user.photoURL,
            selectedGradient: getDefaultGradient(),
          });
        }
      } catch (error) {
        console.error('Error loading user profile:', error);
        // Set defaults on error
        setUserProfileData({
          isUsingGradient: !user.photoURL,
          selectedGradient: getDefaultGradient(),
        });
      }
    };

    loadUserProfile();
  }, [user]);

  // Listen for profile updates from Profile page
  useEffect(() => {
    const handleProfileUpdate = async () => {
      if (!user) return;
      
      try {
        const userDocRef = doc(db, 'userProfiles', user.uid);
        const userDoc = await getDoc(userDocRef);
        
        if (userDoc.exists()) {
          const profileData = userDoc.data() as UserProfileData;
          setUserProfileData(profileData);
        }
      } catch (error) {
        console.error('Error refreshing user profile:', error);
      }
    };

    // Add event listener for profile updates
    window.addEventListener('profileUpdated', handleProfileUpdate);
    
    // Cleanup event listener
    return () => {
      window.removeEventListener('profileUpdated', handleProfileUpdate);
    };
  }, [user]);

  // Refresh profile data when navigating away from profile page
  useEffect(() => {
    const loadUserProfile = async () => {
      if (!user) return;
      
      try {
        const userDocRef = doc(db, 'userProfiles', user.uid);
        const userDoc = await getDoc(userDocRef);
        
        if (userDoc.exists()) {
          const profileData = userDoc.data() as UserProfileData;
          setUserProfileData(profileData);
        }
      } catch (error) {
        console.error('Error refreshing user profile:', error);
      }
    };

    // Refresh when leaving profile page
    if (location.pathname !== '/profile') {
      loadUserProfile();
    }
  }, [location.pathname, user]);

  const handleNavigation = (path: string) => {
    navigate(path);
    setSidebarOpen(false);
  };

  const handleSignOut = async () => {
    try {
      await signOut(auth);
      setSidebarOpen(false);
      navigate('/storefront');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const getUserInitials = () => {
    if (user?.displayName) {
      return user.displayName.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
    }
    if (user?.email) {
      return user.email[0].toUpperCase();
    }
    return 'U';
  };

  const getDefaultGradient = () => {
    return `linear-gradient(135deg, ${theme.colors.primary}, ${theme.colors.secondary})`;
  };

  const getProfilePictureProps = () => {
    // If user has uploaded photo and is not using gradient, show the photo
    if (user?.photoURL && !userProfileData.isUsingGradient) {
      return {
        backgroundImage: user.photoURL,
        gradient: undefined,
        showInitials: false,
      };
    }
    
    // Otherwise show gradient with initials
    return {
      backgroundImage: undefined,
      gradient: userProfileData.selectedGradient || getDefaultGradient(),
      showInitials: true,
    };
  };

  const renderIcon = (iconName: string, active: boolean) => {
    const color = active ? '#2c3e50' : '#6c757d';
    const size = 18;
    
    switch (iconName) {
      case 'home':
        return <HomeIcon size={size} color={color} />;
      case 'calendar':
        return <CalendarIcon size={size} color={color} />;
      case 'sun':
        return <SunIcon size={size} color={color} />;
      case 'user':
        return <UserIcon size={size} color={color} />;
      case 'settings':
        return <SettingsIcon size={size} color={color} />;
      case 'bot':
        return <BotIcon size={size} color={color} />;
      default:
        return null;
    }
  };

  const profileProps = getProfilePictureProps();

  return (
    <LayoutContainer>
      <Header>
        <WaveBackground>
          <HeaderWaves />
        </WaveBackground>
        <HeaderContent>
          <MobileLeftSection>
            <MobileMenuButton isOpen={sidebarOpen} onClick={() => setSidebarOpen(!sidebarOpen)}>
              ☰
            </MobileMenuButton>
          </MobileLeftSection>
          
          <Logo>
            <LogoImage src="/images/MMNHeader.png" alt="NeuroNest" />
          </Logo>
          
          <HeaderActions>
            {/* Removed UserInfo and LogoutButton components */}
          </HeaderActions>
        </HeaderContent>
      </Header>

      <MobileOverlay isOpen={sidebarOpen} onClick={() => setSidebarOpen(false)} />

      <Sidebar className={sidebarOpen ? 'open' : ''}>
        <ProfileSection>
          <ProfilePicture 
            onClick={() => navigate('/profile')}
            backgroundImage={profileProps.backgroundImage}
            gradient={profileProps.gradient}
          >
            {profileProps.showInitials && getUserInitials()}
          </ProfilePicture>
          <ProfileName>
            {user?.displayName || user?.email?.split('@')[0] || 'User'}
          </ProfileName>
        </ProfileSection>

        <NavigationSection>
          <NavigationGrid>
            {navigationItems.map((item) => (
              <NavCard
                key={item.path}
                active={location.pathname === item.path}
                onClick={() => handleNavigation(item.path)}
              >
                <NavIcon>{renderIcon(item.icon, location.pathname === item.path)}</NavIcon>
                <NavLabel>{item.label}</NavLabel>
              </NavCard>
            ))}
          </NavigationGrid>
        </NavigationSection>

        <SignOutSection>
          <SignOutButton onClick={handleSignOut}>
            Sign Out
          </SignOutButton>
        </SignOutSection>
      </Sidebar>

      <MainContent>
        {children}
      </MainContent>
    </LayoutContainer>
  );
};

export default AppLayout; 